import { ref, computed } from 'vue'

interface BeforeInstallPromptEvent extends Event {
  prompt(): Promise<void>
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>
}

// Global state for PWA
const deferredPrompt = ref<BeforeInstallPromptEvent | null>(null)
const isInstalled = ref(false)
const isOnline = ref(typeof navigator !== 'undefined' ? navigator.onLine : true)
const needsRefresh = ref(false)

// Track if SW is registered
let swRegistration: ServiceWorkerRegistration | null = null

/**
 * Composable for PWA functionality
 */
export function usePWA() {
  const canInstall = computed(() => deferredPrompt.value !== null && !isInstalled.value)

  const installApp = async (): Promise<boolean> => {
    if (!deferredPrompt.value) {
      return false
    }

    try {
      await deferredPrompt.value.prompt()
      const { outcome } = await deferredPrompt.value.userChoice

      // Clear the deferred prompt - can only be used once
      deferredPrompt.value = null

      return outcome === 'accepted'
    } catch (error) {
      console.error('Error during PWA install:', error)
      return false
    }
  }

  const dismissInstallPrompt = () => {
    deferredPrompt.value = null
  }

  const checkIfInstalled = () => {
    // Check if running in standalone mode (installed PWA)
    const isStandalone =
      window.matchMedia('(display-mode: standalone)').matches ||
      (window.navigator as { standalone?: boolean }).standalone === true ||
      document.referrer.includes('android-app://')

    isInstalled.value = isStandalone
    return isStandalone
  }

  const updateApp = async () => {
    if (swRegistration?.waiting) {
      // Tell waiting service worker to take over
      swRegistration.waiting.postMessage({ type: 'SKIP_WAITING' })
    }
  }

  return {
    canInstall,
    isInstalled,
    isOnline,
    needsRefresh,
    installApp,
    dismissInstallPrompt,
    checkIfInstalled,
    updateApp,
  }
}

/**
 * Initialize PWA event listeners
 * Should be called once in App.vue or main.ts
 */
export function initPWA() {
  // Handle beforeinstallprompt event
  const handleBeforeInstallPrompt = (e: Event) => {
    e.preventDefault()
    deferredPrompt.value = e as BeforeInstallPromptEvent
  }

  // Handle app installed event
  const handleAppInstalled = () => {
    isInstalled.value = true
    deferredPrompt.value = null
    console.log('PWA was installed')
  }

  // Handle online/offline status
  const handleOnline = () => {
    isOnline.value = true
  }

  const handleOffline = () => {
    isOnline.value = false
  }

  // Handle display mode change
  const handleDisplayModeChange = (e: MediaQueryListEvent) => {
    isInstalled.value = e.matches
  }

  // Set up event listeners
  window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt)
  window.addEventListener('appinstalled', handleAppInstalled)
  window.addEventListener('online', handleOnline)
  window.addEventListener('offline', handleOffline)

  // Listen for display mode changes
  const displayModeQuery = window.matchMedia('(display-mode: standalone)')
  displayModeQuery.addEventListener('change', handleDisplayModeChange)

  // Check initial installed state
  isInstalled.value =
    displayModeQuery.matches ||
    (window.navigator as { standalone?: boolean }).standalone === true

  // Register service worker and handle updates
  if ('serviceWorker' in navigator) {
    navigator.serviceWorker.ready.then((registration) => {
      swRegistration = registration

      // Listen for updates
      registration.addEventListener('updatefound', () => {
        const newWorker = registration.installing
        if (newWorker) {
          newWorker.addEventListener('statechange', () => {
            if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
              // New content is available
              needsRefresh.value = true
            }
          })
        }
      })
    })

    // Handle controller change (when new SW takes over)
    navigator.serviceWorker.addEventListener('controllerchange', () => {
      window.location.reload()
    })
  }

  // Cleanup function
  return () => {
    window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt)
    window.removeEventListener('appinstalled', handleAppInstalled)
    window.removeEventListener('online', handleOnline)
    window.removeEventListener('offline', handleOffline)
    displayModeQuery.removeEventListener('change', handleDisplayModeChange)
  }
}
